import React, { memo, useCallback } from 'react';

import { cn } from '../../../lib/css/className/className';
import { FilterItem, FilterItemProps } from '../FilterItem';
import { FilterChip } from '../FilterChip';
import { Text, TextSize } from '../../Text';
import { TestProps } from '../../../internal/types/testTypes';
import { Checkbox, CheckboxProps, CheckboxSize } from '../../Checkbox';
import { FilterSize } from '../BaseFilterChip/BaseFilterChip';
import { HStack } from '../../Stack';

const cls = cn('FilterCheckbox');

export type FilterCheckboxProps = Pick<FilterItemProps, 'size' | 'color' | 'onRemove'> &
    Pick<CheckboxProps, 'isSelected' | 'defaultSelected' | 'isDisabled' | 'onChange'> &
    TestProps & {
        /**
         * Названия классов для элемента
         */
        className?: string;

        /**
         * Подпись к элементу для screen-readers
         */
        'aria-label'?: string;

        /**
         * Подпись для чекбокса
         */
        title: string;
    };

const checkboxSizeMap: Record<FilterSize, CheckboxSize> = {
    xs: 'xs',
    s: 'xs',
    m: 'm',
} as const;

const textSizeMap: Record<FilterSize, TextSize> = {
    xs: 's',
    s: 'mc',
    m: 'm',
} as const;

/**
 * Компонент для отображения фильтра в качестве чекбокса.
 * Умеет отображать строковое значение внутри бейджа.
 */
export const FilterCheckbox = memo(function FilterCheckbox(props: FilterCheckboxProps) {
    const {
        onChange,
        isSelected,
        className,
        defaultSelected,
        'data-testid': dataTestId = 'FilterCheckbox',
        size,
        isDisabled,
        title,
        ...restProps
    } = props;

    const onChipClick = useCallback(() => {
        onChange?.(!isSelected);
    }, [isSelected, onChange]);

    return (
        <FilterItem
            className={cls({}, [className])}
            data-testid={dataTestId}
            keyChip={
                <FilterChip isDisabled={isDisabled} onClick={onChipClick} data-testid={'FilterCheckbox.Chip'}>
                    <HStack gap="8" alignItems="center">
                        <Checkbox
                            className={cls('checkbox')}
                            onChange={onChipClick}
                            size={checkboxSizeMap[size]}
                            isSelected={isSelected}
                            defaultSelected={defaultSelected}
                            isDisabled={isDisabled}
                            data-testid={`${dataTestId}.Checkbox`}
                            tabIndex={-1}
                        />
                        <Text size={textSizeMap[size]} color="current" isTruncated>
                            {title}
                        </Text>
                    </HStack>
                </FilterChip>
            }
            size={size}
            isDisabled={isDisabled}
            {...restProps}
        />
    );
});
